/**
 * Router setup.
 *
 * Two top-level branches under the root:
 *   - `authShellRoute`: a layout route that mounts the AppShell wrapped in the
 *     AuthBoundary. Every authed page is a child.
 *   - `loginRoute`, `loginTwoFaEnrolRoute`, `loginTwoFaVerifyRoute`: rendered
 *     directly under the root, outside the shell, outside the auth gate.
 *
 * URL-driven filter state lives in the `validateSearch` for /projects.
 */
import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router';
import { AppShell, ShellContent } from '@/components/app-shell';
import { AuthBoundary } from '@/components/auth-boundary';
import { LoginPage } from '@/pages/login';
import { DashboardPage } from '@/pages/dashboard';
import { ProjectsListPage } from '@/pages/projects-list';
import { SuppliersPage } from '@/pages/suppliers';
import { ClientsPage } from '@/pages/clients';
import { ProjectDetailPage } from '@/pages/project-detail';
import { EntityDrilldownPage } from '@/pages/entity-drilldown';
import { KanbanPage } from '@/pages/kanban';
import { InboxPage } from '@/pages/inbox';
import { AuditLogPage } from '@/pages/audit-log';
import { AwaitingActionPage } from '@/pages/awaiting-action';
import { EntityKanbanPage } from '@/pages/entity-kanban';
import { AlertsPage } from '@/pages/alerts';
import { ReceivablesPage } from '@/pages/receivables';
import { ForecastPage } from '@/pages/forecast';
import { CommunicationsPage } from '@/pages/communications';
import { UsersPage } from '@/pages/users';
import { SettingsPage } from '@/pages/settings';
import type { ProjectLifecycleState } from '@/lib/visibility-queries';

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

// -- Auth shell ----------------------------------------------------------
const authShellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'shell',
  component: () => (
    <AuthBoundary>
      <AppShell />
    </AuthBoundary>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => authShellRoute,
  path: '/',
  component: () => (
    <ShellContent>
      <DashboardPage />
    </ShellContent>
  ),
});

const kanbanRoute = createRoute({
  getParentRoute: () => authShellRoute,
  path: '/kanban',
  validateSearch: (raw: Record<string, unknown>) => {
    const out: { q?: string; overdueOnly?: boolean; includeClosed?: boolean } = {};
    if (typeof raw.q === 'string' && raw.q.length > 0) out.q = raw.q;
    if (raw.overdueOnly === true || raw.overdueOnly === 'true') out.overdueOnly = true;
    if (raw.includeClosed === true || raw.includeClosed === 'true') out.includeClosed = true;
    return out;
  },
  component: () => (
    <ShellContent>
      <KanbanPage />
    </ShellContent>
  ),
});

const suppliersRoute = createRoute({
  getParentRoute: () => authShellRoute,
  path: '/suppliers',
  component: () => (
    <ShellContent>
      <SuppliersPage />
    </ShellContent>
  ),
});

const clientsRoute = createRoute({
  getParentRoute: () => authShellRoute,
  path: '/clients',
  component: () => (
    <ShellContent>
      <ClientsPage />
    </ShellContent>
  ),
});

const projectsRoute = createRoute({
  getParentRoute: () => authShellRoute,
  path: '/projects',
  validateSearch: (raw: Record<string, unknown>) => {
    const out: {
      lifecycleState?: ProjectLifecycleState;
      overdueOnly?: boolean;
      q?: string;
      includeClosed?: boolean;
    } = {};
    const allowedStates: ProjectLifecycleState[] = [
      'DFA_RECEIVED',
      'DFA_SUBMITTED',
      'CONTRACT_SIGNED',
      'PENDING_PARTS_APPROVAL',
      'ACTIVE',
      'AWAITING_PAYMENT',
      'CLOSED',
    ];
    if (
      typeof raw.lifecycleState === 'string' &&
      (allowedStates as string[]).includes(raw.lifecycleState)
    ) {
      out.lifecycleState = raw.lifecycleState as ProjectLifecycleState;
    }
    if (raw.overdueOnly === true || raw.overdueOnly === 'true') out.overdueOnly = true;
    if (raw.includeClosed === true || raw.includeClosed === 'true') out.includeClosed = true;
    if (typeof raw.q === 'string' && raw.q.length > 0) out.q = raw.q;
    return out;
  },
  component: () => (
    <ShellContent>
      <ProjectsListPage />
    </ShellContent>
  ),
});

const projectDetailRoute = createRoute({
  getParentRoute: () => authShellRoute,
  path: '/projects/$projectId',
  component: () => (
    <ShellContent>
      <ProjectDetailPage />
    </ShellContent>
  ),
});

const entityDrilldownRoute = createRoute({
  getParentRoute: () => authShellRoute,
  path: '/projects/$projectId/items/$itemId',
  component: () => (
    <ShellContent>
      <EntityDrilldownPage />
    </ShellContent>
  ),
});

const inboxRoute = createRoute({
  getParentRoute: () => authShellRoute,
  path: '/inbox',
  component: () => (
    <ShellContent>
      <InboxPage />
    </ShellContent>
  ),
});

const auditRoute = createRoute({
  getParentRoute: () => authShellRoute,
  path: '/audit',
  component: () => (
    <ShellContent>
      <AuditLogPage />
    </ShellContent>
  ),
});

const usersRoute = createRoute({
  getParentRoute: () => authShellRoute,
  path: '/users',
  component: () => (
    <ShellContent>
      <UsersPage />
    </ShellContent>
  ),
});

const settingsRoute = createRoute({
  getParentRoute: () => authShellRoute,
  path: '/settings',
  component: () => (
    <ShellContent>
      <SettingsPage />
    </ShellContent>
  ),
});

const awaitingActionRoute = createRoute({
  getParentRoute: () => authShellRoute,
  path: '/awaiting-action',
  component: () => (
    <ShellContent>
      <AwaitingActionPage />
    </ShellContent>
  ),
});

const entityKanbanRoute = createRoute({
  getParentRoute: () => authShellRoute,
  path: '/entity-kanban',
  validateSearch: (raw: Record<string, unknown>) => {
    const out: {
      q?: string;
      projectId?: string;
      kind?: 'PART' | 'SUB_PART';
      stage?: number;
      visibleStages?: string;
      minDays?: number;
    } = {};
    if (typeof raw.q === 'string' && raw.q.length > 0) out.q = raw.q;
    if (typeof raw.projectId === 'string' && raw.projectId.length > 0) {
      out.projectId = raw.projectId;
    }
    if (raw.kind === 'PART' || raw.kind === 'SUB_PART') out.kind = raw.kind;

    const stage = Number(raw.stage);
    if (Number.isInteger(stage) && stage >= 1 && stage <= 18) out.stage = stage;

    if (typeof raw.visibleStages === 'string' && raw.visibleStages.length > 0) {
      if (raw.visibleStages === 'none') {
        out.visibleStages = 'none';
      } else {
        const stages = [...new Set(
          raw.visibleStages
            .split(',')
            .map((part) => Number(part.trim()))
            .filter((n) => Number.isInteger(n) && n >= 1 && n <= 18),
        )].sort((a, b) => a - b);
        if (stages.length > 0) out.visibleStages = stages.join(',');
      }
    }

    const minDays = Number(raw.minDays);
    if (Number.isInteger(minDays) && minDays > 0) out.minDays = minDays;
    return out;
  },
  component: () => (
    <ShellContent>
      <EntityKanbanPage />
    </ShellContent>
  ),
});

const alertsRoute = createRoute({
  getParentRoute: () => authShellRoute,
  path: '/alerts',
  component: () => (
    <ShellContent>
      <AlertsPage />
    </ShellContent>
  ),
});

const receivablesRoute = createRoute({
  getParentRoute: () => authShellRoute,
  path: '/receivables',
  component: () => (
    <ShellContent>
      <ReceivablesPage />
    </ShellContent>
  ),
});

const forecastRoute = createRoute({
  getParentRoute: () => authShellRoute,
  path: '/forecast',
  component: () => (
    <ShellContent>
      <ForecastPage />
    </ShellContent>
  ),
});

const communicationsRoute = createRoute({
  getParentRoute: () => authShellRoute,
  path: '/communications',
  // Patch 4 — accept ?tag and ?open from global search deep-linking.
  validateSearch: (raw: Record<string, unknown>) => {
    const out: { tag?: string; open?: string } = {};
    if (typeof raw.tag === 'string' && raw.tag.length > 0) out.tag = raw.tag;
    if (typeof raw.open === 'string' && raw.open.length > 0) out.open = raw.open;
    return out;
  },
  component: () => (
    <ShellContent>
      <CommunicationsPage />
    </ShellContent>
  ),
});

// -- Login (outside the shell) -------------------------------------------
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const routeTree = rootRoute.addChildren([
  authShellRoute.addChildren([
    indexRoute,
    kanbanRoute,
    awaitingActionRoute,
    entityKanbanRoute,
    alertsRoute,
    receivablesRoute,
    forecastRoute,
    communicationsRoute,
    suppliersRoute,
    clientsRoute,
    projectsRoute,
    projectDetailRoute,
    entityDrilldownRoute,
    inboxRoute,
    auditRoute,
    usersRoute,
    settingsRoute,
  ]),
  loginRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export { Outlet };
